<template>
  <div class="page page--profile">
    <div class="page__header">
      <router-link to="/cabinet/profile" class="page__back-link">
        <arrow-button
          class="page__back-link-icon"
          :rotated180="true"
        ></arrow-button>
      </router-link>
      <div class="page__title">
        {{ $t('user-profile.title.change-password') }}
      </div>
    </div>
    <div class="user-form">
      <div class="user-form__loader" v-if="isProfileSaving">
        <loader></loader>
      </div>
      <div class="user-form__fields">
        <div class="user-form__field">
          <text-input
            :title="$t('user-profile.fields.old-password.title')"
            name="password"
            type="password"
            placeholder="********"
            v-model:value="oldPassword"
            :password-show="true"
          ></text-input>
        </div>
        <div class="user-form__field">
          <text-input
            :title="$t('user-profile.fields.new-password.title')"
            name="password"
            type="password"
            placeholder="********"
            v-model:value="newPassword"
            :password-show="true"
          ></text-input>
        </div>
        <div class="password-requirements">
          <div class="password-requirements__title">
            {{ $t('user-profile.hints.change-password.title') }}
          </div>
          <div class="password-requirements__list">
            <div
              class="password-requirements__list-item"
              v-html="$t('user-profile.hints.change-password.min-8')"
            ></div>
            <div
              class="password-requirements__list-item"
              v-html="$t('user-profile.hints.change-password.a-z')"
            ></div>
            <div
              class="password-requirements__list-item"
              v-html="$t('user-profile.hints.change-password.one-upper')"
            ></div>
            <div
              class="password-requirements__list-item"
              v-html="$t('user-profile.hints.change-password.0-9')"
            ></div>
          </div>
        </div>
        <div class="user-form__field">
          <text-input
            :title="$t('user-profile.fields.confirm-password.title')"
            name="password"
            type="password"
            placeholder="********"
            v-model:value="confirmPassword"
            :password-show="true"
          ></text-input>
        </div>
      </div>
      <div class="user-form__save">
        <primary-button
          :disabled="v$.$silentErrors.length > 0"
          class="primary-button"
          @click="changePassword"
          :label="$t('user-profile.actions.save')"
        ></primary-button>
      </div>
    </div>
    <success-notification
      :text="$t('user-profile.notifications.profile-saved-success')"
      ref="profileSavedSuccessNotification"
    ></success-notification>
    <error-notification
      :text="$t('user-profile.notifications.password-update-error')"
      ref="passwordUpdateErrorNotification"
    ></error-notification>
  </div>
</template>

<script>
import { UserApi } from '@js-app/office/api/user-api'
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import Switcher from '@vue-app/components/base/input/Switcher.vue'
import responsiveMixin from '@vue-app/mixins/responsive-mixin'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import { mapGetters } from 'vuex'
import Loader from '@vue-app/components/base/Loader.vue'
import ChangePhoneEmailHint from '@vue-app/components/user/profile/ChangePhoneEmailHint.vue'
import { minLength, required, sameAs } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { textFieldMask } from '@js-app/services/helpers/masks'
import ErrorNotification from '@vue-app/components/base/popup/notification/ErrorNotification.vue'
import {
  existDigit,
  existLowerCase,
  existUpperCase,
} from '@js-app/services/validators'

export default {
  name: 'ChangePassword',
  components: {
    ErrorNotification,
    ChangePhoneEmailHint,
    Loader,
    PrimaryButton,
    SuccessNotification,
    Switcher,
    TextInput,
    ArrowButton,
  },
  mixins: [responsiveMixin],
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      isProfileSaving: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  },
  validations() {
    return {
      oldPassword: { required },
      newPassword: {
        required,
        existDigit,
        existLowerCase,
        existUpperCase,
        minLength: minLength(8),
      },
      confirmPassword: { required, sameAs: sameAs(this.newPassword) },
    }
  },
  created() {},
  TEXT_MASK: textFieldMask,
  computed: {
    ...mapGetters(['userData']),
  },
  watch: {
    userData() {
      this.user = { ...this.userData }
    },
  },

  methods: {
    async handleTFAClick(isChecked) {
      this.isTfaEnabled = isChecked
    },
    async changePassword() {
      let _isValid = await this.v$.$validate()

      if (!_isValid) {
        return
      }

      this.isProfileSaving = true

      try {
        await UserApi.updatePassword(
          this.userData.UserID,
          this.oldPassword,
          this.newPassword
        )
        this.oldPassword = ''
        this.confirmPassword = ''
        this.newPassword = ''

        await this.$refs.profileSavedSuccessNotification.show()
        await this.$router.push('/cabinet/profile')
      } catch (e) {
        await this.$refs.passwordUpdateErrorNotification.show()
      } finally {
        this.isProfileSaving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.password-requirements {
  margin-top: -19px;
  margin-bottom: 30px;
  background: #ffffff;
  border: 1px solid #e0e6f2;
  border-radius: 6px;
  padding: 14px 6px 6px;
  &__title {
    font-weight: 600;
    font-size: 11px;
    line-height: 18px;
    text-align: center;
    color: #465a8b;
    margin-bottom: 14px;
  }

  &__list {
    padding: 13px 11px 19px;
    background: #f0f4fb;
    border-radius: 6px;

    &-item {
      display: flex;
      align-items: center;

      &:before {
        content: '';
        display: block;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: #64748B;
        margin-right: 7px;
      }

      font-weight: 500;
      font-size: 10px;
      line-height: 14px;
      color: #585f6d;
      margin-bottom: 11px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
.page {
  &--profile {
    padding-bottom: 50px;
    margin-top: 60px;
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 40px;
  }

  &__back-link {
    margin-right: 18px;
  }

  &__title {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: #585f6d;
  }
}
.user-form {
  padding: 34px 50px;
  background: #ffffff;
  border-radius: 22px;
  max-width: 390px;
  position: relative;
  &__loader {
    position: absolute;
    border-radius: 22px;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(#ffffff, 0.7);
    z-index: 99;
  }

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #67768a;
    margin-bottom: 40px;
  }

  &__field {
    margin-bottom: 30px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__change-password {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;

    &-button {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #64748B;
    }
  }

  &__tfa {
    margin-top: 40px;

    &-hint {
      font-weight: 500;
      font-size: 10px;
      line-height: 14px;
      color: #b9c4d8;
      margin-top: 8px;
    }
  }

  &__save {
    margin-top: 60px;
    width: calc(100% - 40px);
    margin-left: auto;
    margin-right: auto;
  }
}
.primary-button {
  width: 100%;
  padding: 25px;
  background: #64748B;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
}
@media screen and (max-width: 992px) {
  .page {
    &--profile {
      margin-top: 20px;
    }

    &__title {
      font-weight: 700;
      font-size: 18px;
      line-height: 26px;
      /* identical to box height, or 144% */

      /* text 1 */

      color: #47475e;
    }
  }
}
</style>
